import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PlainDate } from 'temporal-luxon';
import { DatePicker, Input, Select, Spinner, useLocalizeMessage } from 'libs.nucleus';
import { AuthContext } from 'libs.react_internal';
import { useServerSideCubeQuery, } from '../../../data_reports/hooks/use_server_side_cube_query';
import { getCubeApi } from '../../../transfer_templates/hooks/use_cube_api/use_cube_api.hook';
import { NO_VALUE_SENTINEL, PAGE_SIZE, dateRangeFromUnsetOption, getFilterFieldLabel, isDateRangeFromUnset, isDateRangeOperator, isFreeTextOperator, isNoValueOperator, isNumericFieldType, } from '../../utils';
/**
 * Nucleus Select's root is inline-block, which leaves a baseline gap and shifts the
 * control off the value input beside it. Force a full-width flex column so the
 * button lines up with the other filter controls.
 */
export const FilterBarControlWidth = ({ children }) => (_jsx("div", { className: 'flex w-full max-w-full min-w-0 flex-col [&>div]:!flex [&>div]:!w-full [&>div]:!max-w-full [&>div]:!min-w-0 [&>div]:!flex-col', children: children }));
/** Operator picker sits on the same row as the value control and shares its width equally. */
const GroupedOperatorSuffix = ({ children }) => (_jsx("div", { className: 'flex min-w-0 flex-1 flex-col justify-end', children: children }));
export const FilterSelect = ({ filter, studyId, environmentId, runCount, cubeConfigIds, selectedOptions, onChange, label: labelOverride, hideOperatorControl = false, suffix, selectWidth = 'full', selectedOperator, cubeApi: cubeApiOverride, analyticsEnv, }) => {
    const translate = useLocalizeMessage();
    const { entityId } = useContext(AuthContext);
    const isMeasureFilter = filter.role === 'measure';
    // Measures are aggregates. A time-typed MIN/MAX must not fall through to the
    // date picker or the paginated value list, which only make sense for dimensions.
    const isTimeField = filter.fieldType === 'time' && !isMeasureFilter;
    const isNumericField = isNumericFieldType(filter.fieldType) || isMeasureFilter;
    /** Grouped rows pass `label` and pick the operator in a separate control. */
    const operatorIsExternal = hideOperatorControl || labelOverride !== undefined;
    const operatorSuffix = suffix ? _jsx(GroupedOperatorSuffix, { children: suffix }) : null;
    const effectiveOperator = filter.defaultOperator || filter.operator;
    const activeOperator = selectedOperator || effectiveOperator || 'equals';
    // `contains`-style operators take a user-typed substring rather than a known
    // dataset value, so we render a free-text input and skip loading options.
    const isFreeText = isFreeTextOperator(activeOperator);
    const isNoValue = isNoValueOperator(activeOperator);
    const isDateRange = isDateRangeOperator(activeOperator);
    /** Field name used as the value control label (grouped rows pass `label` override). */
    const labelFieldType = isMeasureFilter ? 'number' : filter.fieldType;
    const fieldLabel = useMemo(() => getFilterFieldLabel(labelOverride ?? filter.label, labelFieldType, activeOperator, translate, {
        includeOperatorInLabel: !operatorIsExternal,
    }), [labelOverride, filter.label, labelFieldType, activeOperator, translate, operatorIsExternal]);
    const cubeConfigIdsKey = cubeConfigIds.join(',');
    const filterQueryKey = JSON.stringify(filter.query);
    const mountKey = `${filter.key}|${filterQueryKey}|${studyId}|${environmentId}|${analyticsEnv ?? ''}|${entityId ?? ''}|${cubeConfigIdsKey}|${runCount}`;
    const cubeApi = useMemo(() => {
        if (runCount < 1 || !studyId || !entityId) {
            return null;
        }
        // Study reports: reuse the chart client so option queries follow the selected
        // prod/test host (analytics vs analytics-dev) and X-Env / X-Orgs headers.
        if (cubeApiOverride) {
            if (analyticsEnv !== 'prod' && !environmentId) {
                return null;
            }
            return cubeApiOverride;
        }
        if (!environmentId) {
            return null;
        }
        return getCubeApi({
            entityId: entityId.toString(),
            studyId,
            activeReportsTab: 0,
            selectedTestEnv: { label: '', value: environmentId },
            prodOrgs: [],
            env: 'test',
            cubeConfigIds,
        });
    }, [cubeApiOverride, analyticsEnv, runCount, studyId, environmentId, entityId, cubeConfigIdsKey, cubeConfigIds]);
    const dateValue = useMemo(() => {
        if (!isTimeField || isDateRange || selectedOptions.length === 0) {
            return undefined;
        }
        try {
            return PlainDate.from(selectedOptions[0].value.split('T')[0]);
        }
        catch {
            return undefined;
        }
    }, [isTimeField, isDateRange, selectedOptions]);
    const dateRangeFrom = useMemo(() => {
        if (!isDateRange) {
            return undefined;
        }
        const val = selectedOptions[0]?.value;
        if (!val || isDateRangeFromUnset(val)) {
            return undefined;
        }
        try {
            return PlainDate.from(val.split('T')[0]);
        }
        catch {
            return undefined;
        }
    }, [isDateRange, selectedOptions]);
    const dateRangeTo = useMemo(() => {
        if (!isDateRange) {
            return undefined;
        }
        const val = selectedOptions[1]?.value;
        if (!val) {
            return undefined;
        }
        try {
            return PlainDate.from(val.split('T')[0]);
        }
        catch {
            return undefined;
        }
    }, [isDateRange, selectedOptions]);
    const getDateRangeFromOption = useCallback(() => {
        const opt = selectedOptions[0];
        if (!opt?.value || isDateRangeFromUnset(opt.value)) {
            return null;
        }
        return opt;
    }, [selectedOptions]);
    const getDateRangeToOption = useCallback(() => {
        const opt = selectedOptions[1];
        if (!opt?.value) {
            return null;
        }
        return opt;
    }, [selectedOptions]);
    const handleDateChange = useCallback((date) => {
        if (date) {
            const isoValue = date.toString();
            onChange([{ label: isoValue, value: isoValue }]);
        }
        else {
            onChange([]);
        }
    }, [onChange]);
    const handleDateRangeFromChange = useCallback((date) => {
        const existingTo = getDateRangeToOption();
        if (date) {
            const fromOpt = { label: date.toString(), value: date.toString() };
            onChange(existingTo ? [fromOpt, existingTo] : [fromOpt]);
        }
        else if (existingTo) {
            onChange([dateRangeFromUnsetOption(), existingTo]);
        }
        else {
            onChange([]);
        }
    }, [getDateRangeToOption, onChange]);
    const handleDateRangeToChange = useCallback((date) => {
        const existingFrom = getDateRangeFromOption();
        if (date) {
            const toOpt = { label: date.toString(), value: date.toString() };
            onChange(existingFrom ? [existingFrom, toOpt] : [dateRangeFromUnsetOption(), toOpt]);
        }
        else if (existingFrom) {
            onChange([existingFrom]);
        }
        else {
            onChange([]);
        }
    }, [getDateRangeFromOption, onChange]);
    const selectionLabel = useMemo(() => {
        if (selectedOptions.length === 0) {
            return undefined;
        }
        const labels = selectedOptions.map(({ label }) => label);
        if (labels.length > 1) {
            return `${labels.slice(0, 1).join(', ')} (+${labels.length - 1} more)`;
        }
        return labels.join(', ');
    }, [selectedOptions]);
    const handleSelectChange = useCallback((options) => {
        onChange(options.length > 0 ? options : []);
    }, [onChange]);
    // No-value operators (set / notSet) need no input — the condition is toggled
    // via a hardcoded True/False dropdown (True activates the blank/not-blank
    // condition with a sentinel; False leaves it inactive, matching an unchecked box).
    if (isNoValue) {
        const isActive = selectedOptions.some((o) => o.value === NO_VALUE_SENTINEL);
        const trueOption = { label: translate('True'), value: 'true' };
        const falseOption = { label: translate('False'), value: 'false' };
        return (_jsx("div", { className: 'w-full min-w-0', children: _jsxs("div", { className: 'flex w-full min-w-0 items-end gap-4', children: [_jsx("div", { className: 'flex min-w-0 flex-1 flex-col justify-end', children: _jsx(FilterBarControlWidth, { children: _jsx(Select, { dataTestId: `no-value-filter-${filter.key}`, label: fieldLabel, options: [trueOption, falseOption], value: isActive ? trueOption : falseOption, onChange: (option) => onChange(option.value === 'true' ? [{ label: NO_VALUE_SENTINEL, value: NO_VALUE_SENTINEL }] : []), width: selectWidth }) }) }), operatorSuffix] }) }));
    }
    // Date-range operators need two date pickers (from / to) sharing the full width.
    if (isDateRange) {
        return (_jsxs("div", { className: 'w-full min-w-0', children: [fieldLabel && (_jsx("div", { className: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-mb-2', children: fieldLabel })), _jsxs("div", { className: 'flex w-full min-w-0 items-end gap-4', children: [_jsx("div", { className: 'flex min-w-0 flex-1 flex-col', children: _jsx(DatePicker, { id: `date-range-from-${filter.key}`, label: translate('From'), value: dateRangeFrom, onChange: handleDateRangeFromChange, width: 'full' }) }), _jsx("div", { className: 'flex min-w-0 flex-1 flex-col', children: _jsx(DatePicker, { id: `date-range-to-${filter.key}`, label: translate('To'), value: dateRangeTo, onChange: handleDateRangeToChange, width: 'full' }) }), operatorSuffix] })] }));
    }
    if (isTimeField) {
        return (_jsx("div", { className: 'flex w-full min-w-0 flex-col', children: _jsxs("div", { className: 'flex w-full min-w-0 items-end gap-4', children: [_jsx("div", { className: 'min-w-0 flex-1', children: _jsx(DatePicker, { id: `date-filter-${filter.key}`, label: fieldLabel, value: dateValue, onChange: handleDateChange, width: selectWidth }) }), operatorSuffix] }) }));
    }
    // Numeric fields mirror the free-text field: a single operator renders just
    // the number input (the operator comes from config and is shown in the label),
    // while multiple operators for the same field are grouped and add an operator
    // picker to the right via `operatorSuffix`. Measures use this same path.
    if (isNumericField) {
        const numericValue = selectedOptions[0]?.value ?? '';
        return (_jsx("div", { className: 'w-full min-w-0', children: _jsxs("div", { className: 'flex w-full min-w-0 items-end gap-4', children: [_jsx("div", { className: 'flex min-w-0 flex-1 flex-col justify-end', children: _jsx(Input, { id: `numeric-filter-${filter.key}`, dataTestId: `numeric-filter-${filter.key}`, label: fieldLabel, type: 'number', value: numericValue, onChange: (e) => onChange(e.target.value ? [{ label: e.target.value, value: e.target.value }] : []), width: selectWidth }) }), operatorSuffix] }) }));
    }
    // Free-text operators (e.g. `contains`) accept user-typed substrings and don't
    // require a Cube fetch, so we render the Input even before the user clicks Run.
    if (isFreeText) {
        const freeTextValue = selectedOptions[0]?.value ?? '';
        return (_jsx("div", { className: 'w-full min-w-0', children: _jsxs("div", { className: 'flex w-full min-w-0 items-end gap-4', children: [_jsx("div", { className: 'min-w-0 flex-1', children: _jsx(Input, { id: `text-filter-${filter.key}`, label: fieldLabel, value: freeTextValue, onChange: (e) => {
                                const next = e.target.value;
                                onChange(next ? [{ label: next, value: next }] : []);
                            }, width: selectWidth }) }), operatorSuffix] }) }));
    }
    if (!cubeApi) {
        // No Cube calls until study/env are set and the user has run preview at least once (matches chart cards).
        return (_jsx("div", { className: 'relative w-full min-w-0', children: _jsxs("div", { className: 'flex w-full min-w-0 items-end gap-4', children: [_jsx("div", { className: 'min-w-0 flex-1', children: _jsx(FilterBarControlWidth, { children: _jsx(Select, { label: fieldLabel, options: [], value: selectedOptions, onChange: handleSelectChange, selectionLabel: selectionLabel, width: selectWidth, multiple: true, searchableOptions: true, searchPlaceholder: translate('Search and press Enter...') }, filter.key) }) }), operatorSuffix] }) }));
    }
    return (_jsx(FilterSelectPaginated, { filter: filter, fieldLabel: fieldLabel, cubeApi: cubeApi, selectedOptions: selectedOptions, onChange: onChange, translate: translate, selectWidth: selectWidth, suffix: operatorSuffix }, mountKey));
};
const FilterSelectPaginated = ({ filter, fieldLabel, cubeApi, selectedOptions, onChange, translate, selectWidth = 'full', suffix, }) => {
    const [paginationState, setPaginationState] = useState({
        pageIndex: 0,
        pageSize: PAGE_SIZE,
    });
    const [accumulatedOptions, setAccumulatedOptions] = useState([]);
    const [exhausted, setExhausted] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const activeSearchRef = useRef('');
    const prevResultRef = useRef(null);
    const waitingForLoadMoreRef = useRef(false);
    const loadMoreResolveRef = useRef(null);
    const query = useMemo(() => {
        const baseQuery = { ...filter.query, renewQuery: true };
        const filters = [...(baseQuery.filters || [])];
        if (searchTerm.trim()) {
            filters.push({
                member: filter.key,
                operator: 'contains',
                values: [searchTerm.trim()],
            });
        }
        return { ...baseQuery, filters };
    }, [filter.query, filter.key, searchTerm]);
    const { resultSet, isLoading, error, total } = useServerSideCubeQuery(query, {
        pagination: paginationState,
        cubeApi,
    });
    const hasMore = !exhausted && total > 0 && accumulatedOptions.length < total;
    useEffect(() => {
        if (!isLoading && waitingForLoadMoreRef.current && loadMoreResolveRef.current) {
            waitingForLoadMoreRef.current = false;
            const resolve = loadMoreResolveRef.current;
            loadMoreResolveRef.current = null;
            resolve();
        }
    }, [isLoading]);
    useEffect(() => {
        if (!resultSet || resultSet === prevResultRef.current) {
            return;
        }
        prevResultRef.current = resultSet;
        const rawRows = resultSet.tablePivot();
        const newOptions = rawRows
            .map((row) => {
            const value = row[filter.key];
            if (value === null || value === undefined) {
                return null;
            }
            return { label: String(value), value: String(value) };
        })
            .filter(Boolean);
        const isLastPage = rawRows.length < PAGE_SIZE;
        if (paginationState.pageIndex === 0) {
            setExhausted(isLastPage);
            if (filter.defaultValues?.length) {
                const loadedValueSet = new Set(newOptions.map((o) => o.value));
                const missingDefaults = filter.defaultValues
                    .filter((v) => !loadedValueSet.has(v))
                    .map((v) => ({ label: v, value: v }));
                setAccumulatedOptions([...missingDefaults, ...newOptions]);
            }
            else {
                setAccumulatedOptions(newOptions);
            }
        }
        else {
            if (newOptions.length > 0) {
                setAccumulatedOptions((prev) => [...prev, ...newOptions]);
            }
            if (isLastPage || newOptions.length === 0) {
                setExhausted(true);
            }
        }
    }, [resultSet, filter.key, filter.defaultValues, paginationState.pageIndex]);
    const handleLoadMore = useCallback(async () => {
        if (!hasMore || isLoading) {
            return;
        }
        waitingForLoadMoreRef.current = true;
        return new Promise((resolve) => {
            loadMoreResolveRef.current = resolve;
            setPaginationState((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
        });
    }, [hasMore, isLoading]);
    const handleLoadMoreRef = useRef(handleLoadMore);
    handleLoadMoreRef.current = handleLoadMore;
    const stableLoadMore = useCallback(() => handleLoadMoreRef.current(), []);
    const handleSearchOptions = useCallback((term) => {
        if (term === activeSearchRef.current) {
            return;
        }
        activeSearchRef.current = term;
        if (waitingForLoadMoreRef.current && loadMoreResolveRef.current) {
            waitingForLoadMoreRef.current = false;
            const resolve = loadMoreResolveRef.current;
            loadMoreResolveRef.current = null;
            resolve();
        }
        setExhausted(false);
        setPaginationState({ pageIndex: 0, pageSize: PAGE_SIZE });
        setAccumulatedOptions([]);
        setSearchTerm(term);
    }, []);
    const selectionLabel = useMemo(() => {
        if (selectedOptions.length === 0) {
            return undefined;
        }
        const labels = selectedOptions.map(({ label }) => label);
        if (labels.length > 1) {
            return `${labels.slice(0, 1).join(', ')} (+${labels.length - 1} more)`;
        }
        return labels.join(', ');
    }, [selectedOptions]);
    const handleSelectChange = useCallback((options) => {
        onChange(options.length > 0 ? options : []);
    }, [onChange]);
    const isInitialLoad = isLoading && accumulatedOptions.length === 0;
    return (_jsxs("div", { className: 'relative w-full min-w-0', children: [isInitialLoad && (_jsx("div", { className: 'absolute z-[5] inset-0 flex items-end justify-center size-full pb-1', children: _jsx(Spinner, {}) })), !isLoading && error && (_jsxs("div", { className: 'max-w-[250px] nucleus-font-sans nucleus-text-sm nucleus-text-red-500', children: [translate('No data'), " -- ", error?.message] })), !error && (_jsxs("div", { className: 'flex w-full min-w-0 items-end gap-4', children: [_jsx("div", { className: 'min-w-0 flex-1', children: _jsx(FilterBarControlWidth, { children: _jsx(Select, { disabled: isInitialLoad, label: fieldLabel, options: accumulatedOptions, value: selectedOptions, onChange: handleSelectChange, selectionLabel: selectionLabel, width: selectWidth, multiple: true, searchableOptions: true, searchPlaceholder: translate('Search and press Enter...'), onSearchOptions: handleSearchOptions, onLoadMore: hasMore ? stableLoadMore : undefined }, filter.key) }) }), suffix] }))] }));
};
